import { useCallback, useEffect, useRef, useState } from "react";
import { reverseGeocode } from "../services/geocoding-service";
import type {
	UseAddressResolverProps,
	UseAddressResolverReturn,
} from "../types";

export const useAddressResolver = ({
	latitude,
	longitude,
	initialAddress,
}: UseAddressResolverProps): UseAddressResolverReturn => {
	const normalizedInitialAddress = initialAddress?.trim()
		? initialAddress
		: null;
	const [address, setAddress] = useState<string | null>(
		normalizedInitialAddress
	);
	const [isLoading, setIsLoading] = useState(false);
	const isRequestInFlight = useRef(false);

	const fetchAddress = useCallback(
		async (force = false) => {
			if (!(latitude && longitude) || isRequestInFlight.current) {
				return;
			}

			const hasAddress = address !== null && address.trim() !== "";
			if (!force && hasAddress) {
				return;
			}

			isRequestInFlight.current = true;
			setIsLoading(true);
			try {
				const resolvedAddress = await reverseGeocode(latitude, longitude);
				setAddress(resolvedAddress);
			} catch {
				setAddress("Erro ao carregar endereço");
			} finally {
				isRequestInFlight.current = false;
				setIsLoading(false);
			}
		},
		[address, latitude, longitude]
	);

	useEffect(() => {
		setAddress(normalizedInitialAddress);
	}, [normalizedInitialAddress]);

	const fetchIfMissing = useCallback(() => {
		fetchAddress(false).catch(() => {
			// error state is already handled inside fetchAddress
		});
	}, [fetchAddress]);

	const refetch = useCallback(() => {
		if (!(latitude && longitude)) {
			return;
		}

		setAddress(null);
		fetchAddress(true).catch(() => {
			// error state is already handled inside fetchAddress
		});
	}, [fetchAddress, latitude, longitude]);

	return { address, isLoading, fetchIfMissing, refetch };
};
